// src/components/manual/content.tsx
// 各タブの「マニュアル(見方)」コンテンツのレジストリ。
//   - 読者 = 院内の管理職・事務職(データ分析の専門家ではない)。
//   - 表現規約: 金額は億円/万円で例示・英語jargon禁止(「絞り込み」「内訳」等の日本語)・
//     主語は診療行為/患者数などの事実。
//   - 正確性最優先: 各記述は panel コンポーネントの表示文字列と work-wiki(dashboard-family /
//     byosho-surgery-kpi / income-sokuhou)・ratioMetrics.ts / budgetSpec.ts で裏取りしたものだけを書く。
//   - このファイルは表示専用の静的データ。store への依存は型(TabKey)のみ。
import type { ReactNode } from "react";
import type { TabKey } from "../../store";
import { PROFIT_BASIS_DISCHARGE_ENABLED } from "../../store";
import { IS_THETA, gpText } from "../../lib/gpMode";

export interface ManualTerm {
  /** 画面に出る指標名・列名・語 */
  term: string;
  /** その意味(平易な日本語) */
  desc: ReactNode;
}

export interface ManualContent {
  /** 表示名(TabNav の label に合わせる) */
  label: string;
  icon: string;
  /** このタブで分かること(2-3文) */
  summary: ReactNode;
  /** データの源(平易に) */
  dataSource: ReactNode;
  /** 用語解説(辞書形式) */
  terms: ManualTerm[];
  /** 読み方の注意(重要な罠) */
  cautions: ReactNode[];
  /** 更新タイミング */
  update: ReactNode;
}

// ── 複数タブで共通して出てくる注意(粗利/GP の性質)。文言を一箇所に集約して重複を防ぐ。 ──
//   正式版GPは医師人件費まで引くため、旧定義との違いは表記だけではない。
const CAUTION_GP: ReactNode = IS_THETA ? (
  <>
    <strong>「GP」は「入院収入 − 出来高換算薬剤費 − 出来高換算材料費 − 医師人件費」。仕入れ値を引いた儲けではない。</strong>
    薬剤費・材料費は、電子カルテに仕入れ値が無いため、出来高で計算したときの診療報酬額(出来高換算額)で代用している。
    ステント・弁などの高額材料を使う治療は、収入に材料代が入ると同額が控除されるため GP への寄与が小さく出る。
    これは計算上の性質であって、採算が悪いという意味ではない。
    医師人件費は診療科ごとの人件費を按分したもので、<strong>医師人件費が揃う 2025年4月以降の月しか GP を出せない</strong>。
  </>
) : (
  <>
    <strong>「粗利」は「収入 − 薬剤・材料の出来高換算額」であって、仕入れ値を引いた儲けではない。</strong>
    ステント・弁などの高額材料を使う治療は、収入に材料代が入ると同額が控除されるため粗利への寄与が小さく出る。
    これは仕入れ値が電子カルテに無いことによる計算上の性質で、採算が悪いという意味ではない。
  </>
);

// ── 実体。TabKey をキーにした静的レジストリ。 ──
const CONTENT: Partial<Record<TabKey, ManualContent>> = {
  // 1) 入院実績(前年度・予算比較)
  "入院実績(前年度・予算比較)": {
    label: "入院実績",
    icon: "📈",
    summary: (
      <>
        指定した月の範囲について、今年度の実績・予算・前年度実績を「1ヶ月あたりの平均」でそろえて比べる。
        病院全体と診療科ごとに、入院収入・患者数・単価・平均在院日数などの主要指標が予算に届いているか、
        前年より伸びたかを一目で確認できる。年度12ヶ月のグラフと、月別・科別の一覧をA4横1枚に収めて印刷できる。
      </>
    ),
    dataSource: (
      <>
        電子カルテの会計データ(入院の収入・患者数)、経営企画部が作成した年度予算表、前年度の同じ月の実績。
        手術室件数だけは手術台帳から取り込む。
      </>
    ),
    terms: [
      { term: "実績 / 予算 / 前年度", desc: "今年度の実際の値 / 年度予算 / 昨年度の同じ月の実際の値。すべて指定期間の1ヶ月あたり平均で表示。予定・緊急で絞った予算は、前年同月の診療科・指標別構成比で元予算を配分した推計値。前年の構成比が無いときは、予算の予定入院率を使う。" },
      { term: "予算比 / 前年比", desc: "実績が予算・前年度の何%か。" },
      { term: "達成度(A / B / C)", desc: "A=予算達成、B=予算に届かないが前年を上回った、C=それ以外。" },
      { term: "入院収入", desc: "入院に係る収入。金額は自動で億円/万円に切り替えて表示する。" },
      { term: "入院患者延数(人日)", desc: "入院していた患者を日ごとに数えて足し上げた延べ人数。退院日を含む。" },
      { term: "在院患者延数(人日)", desc: "24時時点で在院していた患者の延べ人数。退院日を含まない。" },
      { term: "1日入院単価(円/日)", desc: "入院収入 ÷ 入院患者延数。患者1人1日あたりの収入。" },
      { term: "1日在院単価(円/日)", desc: "入院収入 ÷ 在院患者延数。" },
      { term: gpText("粗利 / 1日粗利単価"),
        desc: IS_THETA
          ? "入院収入から薬剤・材料の出来高換算額と医師人件費を差し引いた額と、その延数あたりの額(円/日)。下の注意を参照。"
          : "収入から薬剤・材料の出来高換算額を差し引いた額と、その延数あたりの額(円/日)。下の注意を参照。" },
      { term: "新規入院数", desc: "その月に新しく入院した患者数(人)。予定/緊急で絞ると見出しは「予定入院数」/「緊急入院数」になる。" },
      { term: "新規受持数", desc: "新規入院数 + 他科からの転科。診療科ごとに見るときの一貫した指標(病院全体では転科が相殺され新規入院数と一致)。予定/緊急で絞ると、転科も元の入院区分に従い「新規受持数（予定）」/「新規受持数（緊急）」と表示する。" },
      { term: "予定入院率 / 緊急入院率", desc: "新規入院全体に占める予定入院・緊急入院の割合。予定/緊急で絞り込んでも分母は絞り込み前の新規入院数。予算は年度予算表の予定入院数・緊急入院数を新規入院数で割る。" },
      { term: "紹介入院率", desc: "紹介ありの新規入院 ÷ 新規入院数。" },
      { term: "平均在院日数", desc: "在院患者延数 ÷ ((新規入院数+転科入+転科出+退院患者数)÷2)。短いほど良い指標なので色分けが反転する。" },
      { term: "DPCⅡ以内率", desc: "入院日数のうち、DPC入院期間IIまでに収まっている日数の割合(日数ベース。患者数の割合ではない)。" },
      { term: "手術麻酔収入", desc: "会計上の手術区分の収入。手術・麻酔などを束ねた金額。" },
      { term: "手術室件数", desc: "手術台帳に基づく手術の件数。" },
      { term: "常勤医師数 / 専攻医数", desc: "入院診療科の常勤換算の医師数と、うち専攻医の数。" },
    ],
    cautions: [
      <>
        <strong>「予定/緊急」は行によって意味が違う。</strong>
        手術麻酔収入の予定/緊急は「その患者の入院が予定か緊急か」、手術室件数の予定/緊急は「その手術自体が緊急手術か」。
        予定入院の患者が予定手術を受けることもあり、両者は一致しない。「緊急入院の手術件数」ではない。
      </>,
      CAUTION_GP,
      <>
        期間をまたいだ単価・率(1日単価・平均在院日数・DPCⅡ以内率など)は、月ごとの比を単純平均せず、
        分子の合計 ÷ 分母の合計(加重平均)で計算している。
      </>,
      <>
        中/大分類にすると、救急外科などの収入は在院した主治医の科(総合診療科・外科など)に振り分けられる。
        「救急外科」という科は小分類にだけ存在する。
      </>,
      <>麻酔科は入院の診療科ではないため画面には出ない(印刷・Excelには含む)。</>,
    ],
    update: "月次(毎月の会計確定後に集計をビルド)。",
  },

  // 2) 行為別売上
  "行為別売上": {
    label: "行為別売上",
    icon: "💴",
    summary: (
      <>
        入院収入を「入院料 / 手術(輸血除く) / その他」の3区分に分けて、どの区分がいくら売り上げているかを見る。
        主要4指標のカード(入院収入・入院料・手術(輸血除く)・その他。期間の月平均と前年度同期間比)・
        直近13ヶ月(前年同月〜最新月)の積み上げ棒グラフ・診療科×月の一覧(年度12ヶ月)で構成。期間はサイドバーの開始月/終了月で
        指定する(入院実績タブと共通)。3区分の合計は入院収入と一致し、診療科ごとの金額も入院実績タブの入院収入と一致する。
      </>
    ),
    dataSource: (
      <>
        電子カルテの会計データ(入院の行為別集計)。
        薬剤材料費差引後に切り替えたときの控除額は、「診療統計データ詳細明細(EF形式)」の
        <strong>包括前</strong>の点数から算出する(2026-08-22変更。以前は会計RPの実請求額で、
        DPCで包括された薬剤が請求額0のため引けていなかった)。
      </>
    ),
    terms: [
      { term: "入院料", desc: "入院基本料 (点数表区分番号 kouseicd が A1 または A4 で始まる。短期滞在手術等基本料3 (A4) を含む) と 特定入院料 (A3 で始まる。HCU / ICU・回復期リハビリ病棟入院料など) の合計。" },
      { term: "手術(輸血除く)", desc: "行為別分類の手術/手術材料/手術薬剤。輸血と麻酔は「その他」に入る。" },
      { term: "その他", desc: "上記以外(処置・検査・注射・投薬・画像診断・食事・自費など)と麻酔。" },
      { term: "入院収入(総額)", desc: "3区分の合計。自費・食事を含む。" },
      { term: "表示指標", desc: "カードや棒グラフのセグメントをクリックすると、入院収入(総額)/入院料/手術(輸血除く)/その他 に切り替わる。" },
      { term: "収入の基準(入院収入 / 薬剤材料費差引後)",
        desc: "「薬剤材料費差引後」にすると材料・薬剤の出来高換算額を差し引いた額で表示する。"
          + (IS_THETA ? "★このタブは行為区分ごとに医師人件費を割り振れないため、医師人件費は引いていない(=GPではない)。" : "")
          + "入院実績・DPC分析と共通のサイドバー設定で、タブを移っても引き継がれる。" },
      { term: "期間(開始月/終了月)", desc: "サイドバーで年度内の開始月と終了月を選ぶ(入院実績タブと同じ設定・タブを移っても引き継ぐ)。カードの値と一覧の「月平均」列はこの期間の月平均(データのある月のみが分母)。前年比は前年度の同じ月の平均と比べる。" },
      { term: "年度", desc: "最新確定月の年度(4月〜翌3月)に固定。カードの折れ線と一覧は年度12ヶ月を並べ、期間に含む月を枠で示す。棒グラフだけは直近13ヶ月(前年同月〜最新月)で、期間に含む月を帯で示す。" },
      { term: "構成比", desc: "棒グラフのセグメントをクリックすると、金額表示⇔構成比(%)表示が切り替わる。" },
      { term: "診療科(素分類)", desc: "回復期_ を除いた元の科。" },
    ],
    cautions: [
      <>
        <strong>ここでの「手術(輸血除く)」は麻酔も含まない(輸血・麻酔はいずれも「その他」)。</strong>
        入院実績タブの「手術麻酔収入」は手術と麻酔を束ねた金額なので、同じ「手術」でも範囲が違う。
      </>,
      <>診療科×月の一覧の金額は千円単位。</>,
      <>
        「その他」には初診・再診・在宅・労災分も含む(2026-09-07 以前はこれらが患者の科に付かず
        救急科に寄っていたため、科別の金額が入院実績タブと月十数万円ずれていた)。
      </>,
      CAUTION_GP,
      <>回復期リハビリ病棟では手術を算定しないため、その科の手術区分が0になるのは正常。</>,
    ],
    update: "月次。",
  },

  // 3) 手術売上
  "手術売上": {
    label: "手術売上",
    icon: "💴",
    summary: (
      <>
        診療科ごとの手術売上と、手術(Kコード)ごとの売上構成を積み上げ横棒で比較する。
        会計上の件数・総金額・手技・材料・薬剤と、1件あたりの金額分布も一覧で確認できる。
        手術は行為別分類の手術/手術材料/手術薬剤で、輸血・麻酔は含まない。行為別売上タブの「手術(輸血除く)」区分と金額が一致する。
      </>
    ),
    dataSource: (
      <>
        電子カルテの会計データ(会計RP=請求のかたまり単位の明細)。手術のKコードと点数は診療行為マスタから取り込む。
      </>
    ),
    terms: [
      { term: "手術会計件数", desc: "手術の会計(RP)の件数。手術台帳の件数とは別物。画面では期間の月平均で表示する。" },
      { term: "手術総金額", desc: "手技+材料+薬剤の合計(円)。画面では期間の月平均で表示する。" },
      { term: "手技金額", desc: "手術手技の金額。材料・薬剤を除いたもの(=薬剤材料費差引後)。" },
      { term: "材料金額 / 薬剤金額", desc: "その手術に使った材料・薬剤の金額。" },
      { term: "手術（Kコード）", desc: "手術別集計では、1つの手術RPの中で最も点数の高い手技のKコードを使う。同じKコードに複数のレセ電コード（診療行為コード）がある場合は、候補・棒・表を1件にまとめ、金額と件数を合算する。" },
      { term: "Kコード", desc: "診療報酬の手術の区分コード。内部のレセ電コードが改定や術式の違いで複数あっても、画面ではKコードを手術のまとまりとして扱う。" },
      { term: "R6点数 / R8点数", desc: "令和6年度(2024年度) / 令和8年度(2026年度)改定の一般の点数(点)。空欄=その年度のマスタに該当コードがない。手術別の金額分布と件数の推移で同じ列名。" },
      { term: "手技金額/件・材料金額/件", desc: "1件あたりの金額。中央値[第1四分位–第3四分位]で表示。" },
      { term: "診療科", desc: "入院主科(手術日の在院科)。行為別売上と同じ振り分け。" },
      { term: "診療科別と手術別の合計差", desc: "手術別グラフの初期表示はTOP20で、全術式合計ではない。全術式を合計しても、診療科別はKコードを持たない手術関連RPを含み、手術別は主たる手術Kコードを特定できたRPだけなので、その分だけ小さくなる。輸血はどちらにも含めない。" },
      { term: "手術売上 / 手技売上 / 材料薬剤費", desc: "サイドバーで、手技+材料・薬剤の手術総額、手技金額だけ、材料+薬剤だけの表示を切り替える。棒グラフ内の緑色の手技または青色の材料・薬剤をクリックしても切り替わり、選択中の棒を再クリックすると手術売上へ戻る。診療科・術式とも選択金額の降順になる。" },
      { term: "術式検索", desc: "サイドバーでKコードまたは術式名の一部を入力すると、診療科別・手術別グラフと金額分布表を同じ条件で絞り込む。入力候補は選択中の期間・診療科に実績があるKコードを1件ずつ表示し、検索時は配下のすべての診療行為名称を対象にする。" },
      { term: "積み上げ / 100%積み上げ", desc: "初期表示は100%積み上げ。棒内は「金額(構成比)」で、各行の合計が1億円以上なら億円、未満なら万円に統一する。手技と材料・薬剤の2区分を比較し、積み上げへ切り替えると棒の長さで手術総金額も比較できる。" },
      { term: "1件あたり / 月平均", desc: "手術別グラフの金額基準。1件あたりは期間合計を手術会計件数で割った平均、月平均は期間合計を月数で割った1か月あたりの金額。切り替えた基準で上位15件と棒の長さ・右端金額を更新する。" },
      { term: "TOP20", desc: "指定条件における上位20 Kコード。手術売上/手技売上/材料薬剤費と、1件あたり/月平均の選択に応じた金額の降順。右端に選択中の金額と手術会計件数(月平均)を表示する。表でKコードを選ぶと、選んだ手術のグラフへ切り替わり、月次推移もその手術だけの推移になる。" },
      { term: "月次推移の連動", desc: "手術を1つ以上選ぶと、月次推移のグラフもその手術だけの推移に切り替わる(見出しの横に選択中の手術を表示)。選択を解除すると全体へ戻る。診療科のフォーカスや複数科の比較にも連動する。" },
      { term: "手術別 件数の月次推移", desc: "行=Kコード(上の手術別グラフと同じ術式)、列=年月、値=手術件数の表。列は直近12ヶ月(選択期間が12ヶ月より長ければ期間全体)を常に表示し、選択中の期間の月だけ見出しを赤字にする。一番上は「全術式 計」で、表示中の術式の小計ではなく、いまの絞り込み条件での全Kコードの合計。" },
      { term: "件数の「まるめ」の扱い", desc: "この表の件数だけは、金額0の「まるめ」RP(短期滞在手術等基本料3 等)を含む実勢件数。売上の表示は従来どおりまるめを除外するので、手術別の金額分布の「手術会計件数」とは一致しない。2026年度改定でまるめの対象が広がり、2026年6月以降は月72〜93件がこれに当たる(水晶体再建術・腹腔鏡下鼠径ヘルニア(両側)・内視鏡的大腸ポリープ切除など)。除外したままだと実施した手術が0件に見えるため、件数だけ母集団を変えている。" },
      { term: "件数表の「—」", desc: "0件ではない。理由は3つあり、セルにマウスを載せると区別できる。(1)その月のデータがまだ入っていない (2)そのKコードが当月の点数マスタに無い(改定で番号が付け替わった。例=K178-4→K178-42、点数は同じ) (3)件数データが取得できていない。「—」の月は月平均の分母からも外す。" },
      { term: "実績(月平均)", desc: "選択期間(開始月〜終了月)のうち「数えられる月」だけの平均。実績0の月は分母に数えるが、上の「—」の月は分母から外すので、行によって分母が変わる。入院実績の集計表と同じ考え方で、サイドバーの開始月/終了月に連動する。" },
      { term: "複数選択", desc: "通常クリックは1件選択。Ctrlキー(Macは⌘キー)を押しながらクリックすると、診療科やKコードを複数選択して比較できる。" },
      { term: "印刷", desc: "A4横。並びは 診療科別 → 手術別TOP20 → 月次推移 → 件数の推移 → 金額分布。診療科を選ぶと診療科別が1行になるので、診療科別と手術別TOP20を1枚にまとめる。月次推移と件数の推移も1枚に収める。" },
    ],
    cautions: [
      <>手術は手術/手術材料/手術薬剤で、輸血・麻酔は含まない。</>,
      <>金額は円(労災分も円のまま合算)。</>,
      <>
        <strong>金額はすべて「期間の月平均」</strong>(期間合計÷データのある月数)。期間を広げても桁が変わらず、月どうしを比べられる。
        診療科別・手術別のグラフと、金額分布の「手技金額(月平均)」「総額(月平均)」が対象。
        <strong>件数も月平均</strong>(期間の件数÷月数)。1か月だけを選んだときはその月の実数と同じになる。
        CSVには月平均に加えて期間合計と月数も出すので、元の金額・件数に戻せる。
      </>,
      <>
        <strong>売上の表示</strong>は、短期滞在手術等でまるめ(金額0)になったRPを除外している。眼科の白内障のように、ある月の該当RPが全て0円になる例がある。
        <strong>件数の推移だけはこれを含む</strong>ので、金額分布の「手術会計件数」とは一致しない。
      </>,
      <>
        <strong>「手術会計件数」は会計単位で、手術件数タブ(手術台帳)の件数とは定義が異なり一致しない。</strong>
      </>,
      <>既定の集計期間は今年度(診療報酬改定をまたがないため)。</>,
    ],
    update: "月次。",
  },

  // 4) 手術(表示名: 手術件数)
  "手術": {
    label: "手術件数",
    icon: "🔪",
    summary: (
      <>
        手術台帳に基づく手術件数を、月次推移・予算比較・診療科別で見る。予定/緊急の構成も確認できる。
      </>
    ),
    dataSource: <>手術室の手術台帳(ope_sera)。予算は経営企画部の手術件数予算。</>,
    terms: [
      { term: "手術件数", desc: "全身麻酔+局所麻酔を含む全手術の件数。" },
      { term: "予定 / 緊急", desc: "手術自体が予定手術か緊急手術か。予定+緊急=手術件数。" },
      { term: "全身麻酔 / 局所麻酔", desc: "麻酔の別。同じ手術件数を分ける別の軸。" },
      { term: "予算", desc: "FY2026以降。グラフは緑実線=実績・橙点線=予算・薄灰線=前年同月。" },
      { term: "12ヶ月移動平均", desc: "各月を末尾とする直近12ヶ月の平均に切り替える表示。" },
      { term: "対前年", desc: "当月 ÷ 前年同月(例: 111.2% → 前年比 +11.2%)。" },
    ],
    cautions: [
      <>
        <strong>ここでの予定/緊急は「手術の緊急性」。</strong>
        入院の予定/緊急(入院実績タブなど)とは意味が違うので混同しない。
      </>,
      <>診療科は執刀科(手術台帳の科)で、入院の主治医の科とは異なる。</>,
      <>
        手術台帳には外来の手術も含まれる。会計上の入院手術の件数(手術売上タブの手術会計件数)とは定義が異なり一致しない。
      </>,
      <>
        脳神経の一部の件数は、別途「脳センター通知表」の値で置き換えている(診断的な脳血管造影を手術から除くため)。
        経営スライドの正版は430件系。
      </>,
    ],
    update: "月次。脳センター通知表(月1・後着)を待たない当月値は、台帳からリアルタイムに算出する。",
  },

  // 5) 入院経路
  "入院経路": {
    label: "入院経路",
    icon: "🚪",
    summary: (
      <>
        新しく入院した患者が、どういう経路(予定/緊急・紹介の有無・どこを経由してきたか)で入院したかを分析する。
        KPIカード・月次集計・積み上げグラフ・診療科別内訳で見られる。
      </>
    ),
    dataSource: <>電子カルテの入院患者一覧(新規入院全件)と、入院経路のデータ。</>,
    terms: [
      { term: "予定 / 緊急", desc: "入院が予定入院か緊急入院か。" },
      { term: "紹介あり / なし", desc: "他院からの紹介の有無。" },
      { term: "経由(病院 / 総CL / その他)", desc: "どこを経由して入院したか。病院・総合クリニック(総CL)・その他(腎クリニック+地域ケア)。" },
      { term: "経路6区分 / 12区分", desc: "経由×予定緊急(6区分)、さらに紹介の有無で分けたもの(12区分)。" },
      { term: "新規入院数", desc: "新しく入院した患者数(人)。" },
      { term: "予定入院率", desc: "予定入院の割合。" },
      { term: "病院経由率", desc: "病院を経由した入院の割合。" },
      { term: "紹介入院率", desc: "紹介ありの新規入院の割合。" },
      { term: "件数 / 構成比", desc: "積み上げグラフの表示を、実数⇔割合で切り替える。" },
    ],
    cautions: [
      <>
        <strong>サイドバーの「経由」「予定緊急」で絞り込むと、このタブの集計・グラフ・表がすべてその区分だけになる。</strong>
        絞り込み中はパネル上部に案内が出る(印刷にも残る)。
      </>,
      <>入院経路のデータが未取得の月は0件扱いになる。欠損月があるとパネル上部に警告が出る。</>,
      <>前年同期比は、率の差をpt(ポイント)、件数の差を%で表示する。</>,
    ],
    update: "月次。入院経路(入院患者一覧)は月次運用で必須の更新項目(未更新だと予定/緊急の集計が止まる)。",
  },

  // 6) DPC分析
  "DPC分析": {
    label: "DPC分析",
    icon: "🔬",
    summary: (
      <>
        {gpText("DPC(診断群分類)ごとに、退院患者ベースで件数・在院日数・収入・粗利・単価をランキングする。")}
        疾患別 → 病態等(7桁目) → 年齢・JCS等(8桁目) → 手術別 → 処置1別 → 処置2別 → 副傷病(13桁目) → 全14桁 と階層をたどって深掘りでき
        (行をクリックすると、その疾患で次に分岐がある桁まで自動で進む)、
        14桁を選ぶと包括点数の階段グラフと1日あたり包括金額の試算が出る。急性期の退院患者が対象。
      </>
    ),
    dataSource: (
      <>
        電子カルテのDPCデータ(様式1・会計)。包括点数は厚生労働省の電子点数表、
        医療機関別係数は電子カルテの月次データから取り込む。
      </>
    ),
    terms: [
      { term: "疾患別 / 病態等 / 年齢・JCS等 / 手術別 / 処置1別 / 処置2別 / 副傷病 / 全14桁", desc: "DPC14桁コードの6桁 / 7桁 / 8桁 / 10桁 / 11桁 / 12桁 / 13桁 / 14桁での集計。7桁目=病態等分類(例: 肺炎の市中/院内)、8桁目=年齢・出生時体重・JCS等(例: くも膜下出血のJCS10未満/以上)。疾患を選んでいるとき、その疾患で分岐しない桁のタブは薄く表示する(2026-09-28追加)。" },
      { term: "DPCコード列", desc: "表示している桁までのDPCコード。太字がその桁で分かれた部分(x=分岐なし)。右の「›」は、行をクリックすると次の分岐へ進めることを示す(2026-09-28追加)。" },
      { term: "絞り込み中(パンくず)", desc: "疾患 › 8桁目 › 手術 … と、どの桁で何を選んだかを表示する。段をクリックすると、その桁から選び直せる。" },
      { term: "処置1分岐 / 処置2分岐 の列", desc: "セルにカーソルを当てると、その分岐に該当する実績処置(11/12桁目を決めた処置)と、併施(分岐外で桁の採番には効かない処置)を分けて表示する。分岐に対応する候補コード(公式対応表)も併記(2026-07-22変更。以前は実施された処置がすべて1つの並びだった)。" },
      { term: "退院数", desc: "急性期を出た入院数(急性期からの退院、または回復期への転棟)。病院からの退院数とは一致しない。" },
      { term: "件数・金額の表示(期間累計/月平均)", desc: "サイドバーで切り替え(既定=期間累計)。主表・サマリ行・ICD別・実績K表の件数と金額がそろって切り替わる。月平均の分母はデータのある月数。入院日数中央値・DPCⅡ以内率・1日単価はどちらでも同じ値(2026-09-28追加。以前は主表だけ月平均でICD別は期間累計だった)。" },
      { term: "入院日数中央値", desc: "急性期在院日数(急性期最終日 − 入院日 + 1)の中央値。" },
      { term: "入院延日数", desc: "急性期在院日数の合計。月平均。2026-09-28 から主表には出さず CSV にだけ入れる(横スクロールなしで収めるため)。" },
      { term: gpText("1日入院単価 / 1日粗利単価"), desc: gpText("患者1人ずつの1日単価(その患者の収入または粗利 ÷ その患者の入院日数)を出し、その中央値をとった額(円/日)。長期入院の外れ値に引っ張られないようにするため、合計÷合計ではない(2026-07-22変更)。") },
      { term: gpText("粗利"),
        desc: IS_THETA
          ? "急性期の収益 − 包括前薬剤費 − 包括前材料費 − 医師人件費。下の注意を参照。"
          : "急性期の収益 − 包括前薬剤費 − 包括前材料費。下の注意を参照。" },
      { term: "DPCⅡ以内率", desc: "期間I・IIの在院日数 ÷ 全在院日数(日数ベース。退院患者の件数割合ではない)。80%以上=緑 / 60〜80%=黄 / 60%未満=赤。" },
      { term: "13桁目 / 14桁目", desc: "副傷病 / 重症度等。" },
      { term: "医療機関別係数", desc: "包括金額の計算に掛ける当院の係数。表示期間の終了月に有効な値を自動適用する。" },
      { term: "包括点数(期間 I / II / III)", desc: "DPC入院期間の階段状の1日あたり点数。" },
    ],
    cautions: [
      <>
        <strong>このタブは「急性期・退院患者ベース」。</strong>在院中の患者は含まない(退院して初めて集計される)。
      </>,
      <>「退院数」は急性期を出た数で、病院からの退院数とは一致しない。</>,
      CAUTION_GP,
      <>DPCⅡ以内率は日数ベース(患者の件数割合ではない)。</>,
      <>DPC改定年度の既定は2026年度(令和8)。年度を切り替えると対象期間も自動で切り替わる。</>,
    ],
    update: "月次(退院患者が確定してから集計)。",
  },

  // 7) 粗利分析(医師別なし・表示名: 粗利分析)
  "粗利分析(医師別なし)": {
    label: gpText("粗利分析"),
    icon: "💹",
    summary: (
      <>
        {gpText("診療科別・DPC別に、粗利・1日粗利単価・粗利率を見て、1日粗利単価で4つの群に分類する。")}
        {/* ★2026-08-27: 退院日基準を封鎖中は 退院数/入院日数中央値/DPC別の深掘り が画面に無いので説明も出さない */}
        {PROFIT_BASIS_DISCHARGE_ENABLED &&
          <>退院日基準では退院数・入院日数中央値も見られる。科をクリックするとDPC別に深掘りできる。</>}
      </>
    ),
    dataSource: (
      <>
        電子カルテの会計・在院データ。薬剤費・材料費の控除額は
        「診療統計データ詳細明細(EF形式)」の<strong>包括前</strong>の点数から算出する
        (DPCで包括された薬剤も出来高で計算し直して引く)。
        {IS_THETA && <> 医師人件費は経営企画部の人件費集計を診療科へ按分したもの。</>}
      </>
    ),
    terms: IS_THETA ? [
      { term: "GP", desc: "入院収入 − 出来高換算薬剤費 − 出来高換算材料費 − 医師人件費。下の注意を参照。" },
      { term: "1日GP単価", desc: "科別・医師別は GP ÷ 患者延数(円/日)。DPC別のみ、患者1人ずつの1日GP単価の中央値(列見出しの ※ 印)。" },
      { term: "群分類(1群 / 2群 / 3群 / 4群)", desc: "1日GP単価で4つに分ける。しきい値はサイドバーで調整できる(DPC別はその行の中央値で判定)。既定値は 85,000 / 75,000 / 65,000 円/日。" },
      { term: "GP率", desc: "GP ÷ 医師人件費 (倍・小数第2位)。医師人件費 1 円あたり何円の GP を生んでいるかを見る。★収入に対する率ではない (2026-08-24 にこの定義へ変更)。分子・分母の両方が期間の全月に揃っているときだけ出す。" },
      { term: "薬剤材料費", desc: "出来高換算薬剤費 + 出来高換算材料費 (千円/月)。仕入れ値は電子カルテに無いので、使った薬剤・材料を出来高で評価した診療報酬額 (EF の包括前。DPCで包括された分も出来高で計算し直す) で代用している。" },
      { term: "医師人件費", desc: "診療科へ按分した医師の給与 (千円/月)。常勤換算>0 の医師だけを計上し、賞与は直近12ヶ月平均で均す。★医師別・DPC別の値は「その医師の給与」「その疾患の医師工数」ではなく、診療科の額を患者日で按分したもの。2025年4月以降のみ。" },
    ] : [
      { term: "粗利", desc: "収入 − 薬剤・材料の出来高換算額。下の注意を参照。" },
      { term: "1日粗利単価", desc: "科別・医師別は 粗利 ÷ 患者延数(円/日)。DPC別のみ、患者1人ずつの1日粗利単価の中央値(2026-07-22変更・列見出しの ※ 印)。" },
      { term: "群分類(1群 / 2群 / 3群 / 4群)", desc: "1日粗利単価で4つに分ける。しきい値はサイドバーで調整できる(DPC別はその行の中央値で判定)。" },
      { term: "粗利率", desc: "粗利 ÷ 収入。" },
      { term: "薬剤材料費", desc: "出来高換算薬剤費 + 出来高換算材料費 (千円/月)。仕入れ値は電子カルテに無いので、使った薬剤・材料を出来高で評価した診療報酬額 (EF の包括前) で代用している。" },
      { term: "医師人件費", desc: "診療科へ按分した医師の給与 (千円/月)。常勤換算>0 の医師だけを計上し、賞与は直近12ヶ月平均で均す。★医師別・DPC別の値は診療科の額を患者日で按分したもの (その医師の給与ではない)。2025年4月以降のみ。★正式版の粗利率は従来どおり 粗利 ÷ 収入 で、この列とは関係しない。" },
      // ★2026-08-27: 退院日基準の封鎖中は「期間基準」の選択肢自体が無いので用語ごと出さない。
      ...(PROFIT_BASIS_DISCHARGE_ENABLED
        ? [{ term: "期間基準(在院日 / 退院日)", desc: "在院日基準=在院した日ごとに帰属(科別のみ)。退院日基準=退院した患者で集計(科別・医師別・DPC別)。" }]
        : []),
      { term: "単価切替(在院 / 入院)", desc: "延数の分母を在院患者延数にするか、入院患者延数にするか。" },
      { term: "医療区分", desc: PROFIT_BASIS_DISCHARGE_ENABLED
          ? "合計 / 急性期 / 回復期。在院日基準でのみ有効(退院日基準は急性期に固定)。"
          : "合計 / 急性期 / 回復期。" },
    ],
    cautions: [
      CAUTION_GP,
      <>
        <strong>{gpText("粗利")}「率」の低下は必ずしも悪化ではない。</strong>
        高額材料が乗ると率は下がるが額は増えることがある。率でなく額で見る。
      </>,
      <>病院全体の行は各科の合計から合成しており、中央値は科別の値からは計算できない(病院全体行のみ別途算出)。</>,
      <>DPC別の絞り込みはDPC別の表にだけ効き、科別・医師別の表には及ばない。</>,
    ],
    update: "月次。",
  },

  // 8) 相関分析
  "相関分析": {
    label: "相関分析",
    icon: "🔗",
    summary: (
      <>
        選んだ入院実績の指標どうしの、月ごとの関係の強さ(相関)を散布図行列で見る。各点が1ヶ月分。
        先頭に置いた指標を基準に、相関の強い順に並ぶ。
      </>
    ),
    dataSource: <>このダッシュボードの月次集計データ(入院収入・患者数・単価・手術・経路などの各指標)。</>,
    terms: [
      { term: "各点=1ヶ月", desc: "散布図の1点が、対象期間の1ヶ月分。" },
      { term: "基準指標(先頭・🎯)", desc: "選択の先頭に置いた指標。他の指標との相関を測る。" },
      { term: "相関 r", desc: "−1〜+1。青=正の相関 / 赤=負の相関、文字の大きさが相関の強さ。" },
      { term: "有意記号(* ** ***)", desc: "*=p<.05、**=p<.01、***=p<.001(統計的に偶然とは言いにくい度合い)。" },
      { term: "上三角 / 下三角 / 対角", desc: "上三角=散布図+回帰線 / 下三角=相関r / 対角=月次推移。" },
      { term: "カテゴリ", desc: "収入 / 行為別収入 / 入院実績 / 手術実績 / 経路 から指標を選ぶ。" },
      { term: "条件セット", desc: "選んだ指標・期間・粒度・診療科の組み合わせを保存・共有できる。" },
    ],
    cautions: [
      <>
        <strong>相関は因果ではない。</strong>関係の強さを見るもので、一方が他方の原因とは限らない。
      </>,
      <>
        比の指標(単価など)とその構成要素(延数など)は分母を共有するため、見かけの相関が出やすい
        (負の相関でも「悪い」証拠にはならない)。
      </>,
      <>サンプルは期間内の月数。期間が短いと相関は不安定になる。</>,
    ],
    update: "月次(元の集計データの更新に連動)。",
  },

  // 9) 経営PL
  "経営PL": {
    label: "PL",
    icon: "💰",
    summary: (
      <>
        病院の損益(PL)を予算・前年と比べる。サマリーのカード、損益トレンド、利益のウォーターフォール、
        予算差額の大きい順ランキングで、収益・費用・利益の状況を見る。月別 / 直近12ヶ月累計 / 当年度累計で切り替えられる。
        利益の段階(医業利益 / 経常利益)で、トレンドとウォーターフォールをどちらの利益で締めるか切り替えられる。
      </>
    ),
    dataSource: <>病院会計のPLデータと、経営企画部のPL予算。対象=狭山地区(埼玉石心会病院)。</>,
    terms: [
      { term: "医業収益計 / 入院収入 / 外来収益", desc: "収益の各項目。" },
      { term: "材料費計 / 人件費計 / 委託費計 / 設備関係費計 / 経費等", desc: "費用の各項目。経費等の中身はサイドバーの利益指標で変わる。" },
      { term: "利益の段階", desc: "医業利益 / 経常利益。損益トレンドとウォーターフォールをどちらの利益で締めるかを切り替える(経常利益を選ぶと医業外損益の段が入る)。サマリーのカードは12枚固定で両方出る。" },
      { term: "医業利益", desc: "医業収益 − 医業費用。本業だけの利益。医業収益計から5つの費用(材料費計・人件費計・委託費計・設備関係費計・経費等)を引いた額。" },
      { term: "経常利益", desc: "医業利益 + 医業外損益。" },
      { term: "医業外損益", desc: "雑収入・受取利息 − 雑損失・支払利息。プラスが収入超。補助金は雑収入に入るので月によって大きく振れる(26年03月は +373,206千円)。" },
      { term: "経費等", desc: "経費計 + 研修費 + 研究費 + 本部経費。医業費用のうち材料費・人件費・委託費・設備関係費以外。" },
      { term: "本部経費", desc: "本部から配賦される経費。病院側では動かせない。経費等 の約16%を占める(予算比較の帳票では「事務局経費」という名前)。" },
      { term: "保険等査定減", desc: "査定で減らされた額。医業収益計はこれを引いた後の純額。値はマイナスで入っており、0に近づく(査定が減る)ほど良い。" },
      { term: "予算比 / 前年比", desc: "実績が予算・前年に対してどうか。利益系は黒字化 / 赤字転落 / 黒字拡大 などの言葉で表示。" },
      { term: "医業収益比", desc: "各項目が医業収益に占める割合。" },
      { term: "差異ランキング", desc: "予算との差額が大きい順のTOP7(良い方向・悪い方向)。" },
      { term: "表示形式", desc: "月別 / 直近12ヶ月累計 / 当年度累計。" },
      { term: "ウォーターフォール", desc: "予算の利益から実績まで、各項目の寄与を積み上げて見せるグラフ。" },
    ],
    cautions: [
      <>金額は千円単位(グラフは百万円)。</>,
      <>
        <strong>色の向きは、緑=良化(収益・利益が予算/前年を上回る、費用が下回る)、赤=悪化。</strong>
        費用は「下回るほど良い」ので色が反転する。
      </>,
      <>
        <strong>補助金は医業外損益に入る。</strong>経費等には入らないので、
        補助金が出た月でも費用の増減はそのまま読める(例: 26年03月は医業外損益 +373,206千円)。
        損益トレンドでは左の柱が 医業収益計＋医業外損益、右の柱が 5つの費用＋利益 で、高さは必ず一致する。
      </>,
      <>
        対象は狭山地区(埼玉石心会病院)。他タブの入院会計データとは集計の枠組みが異なる(全院・入外を含む)。
      </>,
    ],
    update: "月次(PL確定後に集計をビルド)。",
  },
};

// 粗利分析(医師別あり・ベータ)は医師別の切り口が増えるだけで、指標・注意は医師別なし版と同じ。
// 内部キーが別なので同一コンテンツを指す(getManual 側で吸収)。
const ALIASES: Partial<Record<TabKey, TabKey>> = {
  "粗利分析": "粗利分析(医師別なし)",
};

/** タブのマニュアルを取得(未整備タブは undefined)。 */
export function getManual(tab: TabKey): ManualContent | undefined {
  return CONTENT[tab] ?? CONTENT[ALIASES[tab] ?? tab];
}

/** そのタブにマニュアルが用意されているか。 */
export function hasManual(tab: TabKey): boolean {
  return getManual(tab) !== undefined;
}
